/* iter372 — Wizard keyboard shortcuts (Alt+N / Alt+P)
 *
 * Both pieces live in the same file because they share the OS-detection
 * helper and are tightly coupled.
 *
 * <WizardShortcutHandler> — invisible, wires up the global keydown
 *                            listener. Bails when focus is in a text
 *                            input that should swallow the key.
 * <WizardShortcutLegend>  — small chip in the wizard header showing
 *                            the platform-appropriate combo.
 */
import { useEffect } from 'react';
import { Keyboard } from 'lucide-react';

const isMac = () => {
  if (typeof window === 'undefined' || !window.navigator) return false;
  // navigator.userAgentData is the modern API, but coverage is low.
  // Stick with platform for now; if it's "MacIntel" or "iPad" etc, count as Mac.
  return /Mac|iPhone|iPad|iPod/i.test(window.navigator.platform || '');
};

const SWALLOW_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

export function WizardShortcutHandler({ onNext, onPrev, canPrev, canNext }) {
  useEffect(() => {
    const handler = (ev) => {
      // Mac: Option key sets ev.altKey AND remaps `n`→`˜` / `p`→`π`.
      // Match the raw `code` instead so we catch both layouts cleanly.
      if (!ev.altKey) return;
      // Ignore when Ctrl/Cmd is also pressed (preserves browser shortcuts).
      if (ev.ctrlKey || ev.metaKey || ev.shiftKey) return;
      // Skip when typing in inputs that need raw text input.
      // We DO allow number inputs because Sales/Deliveries are heavily
      // number-driven and step-nav is the most useful command there.
      const target = ev.target;
      if (target && SWALLOW_TAGS.has(target.tagName) && !target.readOnly && !target.disabled) {
        if (target.tagName === 'TEXTAREA') return;
        if (target.tagName === 'INPUT' && (target.type === 'text' || target.type === 'search')) return;
      }
      if (ev.code === 'KeyN' && canNext) {
        ev.preventDefault();
        ev.stopPropagation();
        onNext?.();
      } else if (ev.code === 'KeyP' && canPrev) {
        ev.preventDefault();
        ev.stopPropagation();
        onPrev?.();
      }
    };
    // Listen on BOTH window and document with capture so the event is
    // intercepted before any focused number input (Chromium swallows
    // Alt+letter on focused number inputs in some builds — capture on
    // document is the most reliable surface to intercept it).
    window.addEventListener('keydown', handler, { capture: true });
    document.addEventListener('keydown', handler, { capture: true });
    return () => {
      window.removeEventListener('keydown', handler, { capture: true });
      document.removeEventListener('keydown', handler, { capture: true });
    };
  }, [onNext, onPrev, canPrev, canNext]);
  return null;
}

export function WizardShortcutLegend() {
  const mac = isMac();
  const mod = mac ? '⌥' : 'Alt';
  return (
    <div
      className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-slate-50 border border-slate-200 text-[10px] text-slate-600"
      data-testid="wizard-shortcut-legend"
      title={mac ? 'Mac shortcuts' : 'Windows / Linux shortcuts'}
    >
      <Keyboard className="h-3 w-3 text-slate-400" />
      <span className="font-mono">
        <kbd className="px-1 bg-white rounded border">{mod}</kbd>+
        <kbd className="px-1 bg-white rounded border">P</kbd>
      </span>
      <span className="opacity-60">prev</span>
      <span className="opacity-30 mx-0.5">·</span>
      <span className="font-mono">
        <kbd className="px-1 bg-white rounded border">{mod}</kbd>+
        <kbd className="px-1 bg-white rounded border">N</kbd>
      </span>
      <span className="opacity-60">next</span>
    </div>
  );
}
